import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { whiteLabelApi } from '@/services/api'
import { assetUrl } from '@/utils/assetUrl'
import { sanitizeCustomCss } from '@/utils/customCss'

const BrandingContext = createContext(null)

export function useBranding() {
  return useContext(BrandingContext)
}

// How long the app will wait for branding to resolve before rendering
// anyway. Long enough to avoid a flash of unbranded UI on a healthy
// connection, short enough that a hung API is a blip and not a blank page.
const BRANDING_GRACE_MS = 1500

export default function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const graceTimer = useRef(null)

  useEffect(() => {
    // Failsafe: render the app even if branding never resolves.
    graceTimer.current = setTimeout(() => setLoaded(true), BRANDING_GRACE_MS)
    return () => clearTimeout(graceTimer.current)
  }, [])

  useEffect(() => {
    const host = window.location.hostname
    const handleRefresh = async () => {
      try {
        let branding = null
        // First: resolve by domain (custom_domain or subdomain). This
        // is the unauthenticated path patients + login-screen visitors
        // take.
        const res = await whiteLabelApi.resolve(host)
        if (res.data.branding) branding = res.data.branding

        // Fallback: when there's no domain match but the current user
        // is logged in and their clinic has an active white-label
        // config, apply the owner's/clinician's own branding as a
        // preview so the main app (dev.scribenza.com) reflects the
        // settings the clinic just saved. QA filed 'branding not
        // applied after save' because their main-app session never
        // matched the subdomain/custom_domain rule.
        if (!branding && localStorage.getItem('scribenza_token')) {
          try {
            const pv = await whiteLabelApi.preview()
            if (pv.data.branding) branding = pv.data.branding
          } catch {
            /* unauthenticated / no clinic — leave branding null */
          }
        }

        if (branding) {
          if (branding.logo_url)    branding.logo_url    = assetUrl(branding.logo_url)
          if (branding.favicon_url) branding.favicon_url = assetUrl(branding.favicon_url)
          setBranding(branding)
          applyBranding(branding)
        } else {
          setBranding(null)
          applyBranding(null)
        }
      } catch (e) {
        console.error('Branding resolution failed', e)
      } finally {
        clearTimeout(graceTimer.current)
        setLoaded(true)
      }
    }

    window.addEventListener('refresh-branding', handleRefresh)
    handleRefresh()
    return () => window.removeEventListener('refresh-branding', handleRefresh)
  }, [])

  if (!loaded) return null

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  )
}

// White-label favicons are injected as a dedicated <link id="wl-favicon">
// appended after the defaults from index.html, so tearing branding down
// never touches the app's own icons.
const WL_FAVICON_ID = 'wl-favicon'

function applyBranding(b) {
  const root = document.documentElement
  const wlFavicon = document.getElementById(WL_FAVICON_ID)

  if (!b) {
    root.style.removeProperty('--wl-primary')
    root.style.removeProperty('--wl-secondary')
    root.style.removeProperty('--wl-accent')
    root.style.removeProperty('--wl-bg')
    root.style.removeProperty('--wl-surface')
    root.style.removeProperty('--wl-card')

    const customStyle = document.getElementById('wl-custom-css')
    if (customStyle) customStyle.remove()
    if (wlFavicon) wlFavicon.remove()
    document.title = 'Scribenza'
    return
  }

  // Apply CSS custom properties
  if (b.colors) {
    if (b.colors.primary)   root.style.setProperty('--wl-primary', b.colors.primary)
    if (b.colors.secondary) root.style.setProperty('--wl-secondary', b.colors.secondary)
    if (b.colors.accent)    root.style.setProperty('--wl-accent', b.colors.accent)
    if (b.colors.bg)        root.style.setProperty('--wl-bg', b.colors.bg)
    if (b.colors.surface)   root.style.setProperty('--wl-surface', b.colors.surface)
    if (b.colors.card)      root.style.setProperty('--wl-card', b.colors.card)
  }

  // Apply custom CSS
  const existingStyle = document.getElementById('wl-custom-css')
  if (existingStyle) existingStyle.remove()
  const customCss = sanitizeCustomCss(b.custom_css)
  if (customCss) {
    const style = document.createElement('style')
    style.textContent = customCss
    style.id = 'wl-custom-css'
    document.head.appendChild(style)
  }

  // Update favicon. Appended last so it wins over the defaults declared
  // in index.html, which stay in place for when branding is torn down.
  if (b.favicon_url) {
    let link = wlFavicon
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      link.id = WL_FAVICON_ID
      document.head.appendChild(link)
    }
    link.href = b.favicon_url
  } else if (wlFavicon) {
    wlFavicon.remove()
  }

  // Update page title
  if (b.brand_name) {
    document.title = b.brand_name
  }
}
